<template>
  <div class="content mb-5">
    <b-row>
      <b-col class="mb-3">
        <h2>{{ this.$t("User Details") }}</h2>
      </b-col>
    </b-row>
    <b-form @submit="onSubmit" class="mb-5" v-if="!loading">
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group
            id="type"
            label-for="name"
            :label="this.$t('Full Name')"
          >
            <b-form-input
              data-test-id="users-uers-details-name"
              id="name"
              v-model="selectedInternalUser.name"
              readonly
            ></b-form-input>
          </b-form-group>

          <b-form-group
            label-for="description"
            :label="this.$t('Description')"
            :state="$validateState($v.form.description)"
          >
            <b-form-input
              id="description"
              data-test-id="users-uers-details-description"
              v-model="form.description"
              @blur="setValidationInput('description', $event.target.value)"
              :state="$validateState($v.form.description)"
              :placeholder="this.$t('e.g. Marketing')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.description.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group id="email" label-for="email" :label="this.$t('Email')">
            <b-form-input
              data-test-id="users-uers-details-email"
              id="email"
              v-model="selectedInternalUser.email"
              readonly
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12" md="12">
          <b-form-group
            id="policy"
            :label="this.$t('Policy')"
            label-for="policy"
          >
            <t-radio-group
              v-model="form.policy"
              :loading="loading"
              :items="policyRadio"
              :dropdownOptions="policyOptions"
              :selectedItem="form.policyid"
              :selectedItemLabel="form.policyname"
              @change="$v.$touch()"
              @changeDropDown="onDropDownChange"
              @scrollDropDown="scrollDropDown"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <t-save-button
        data-test-id="users-uers-details-save"
        :form-model="$v.form"
        :fetching="fetching"
        @submit="onSubmit"
      />
    </b-form>

    <b-row v-if="loading" class="mb-5">
      <b-col cols="12" md="6">
        <b-skeleton animation="wave" width="85%"></b-skeleton>
        <b-skeleton animation="wave" width="55%"></b-skeleton>
        <b-skeleton animation="wave" width="70%"></b-skeleton>
      </b-col>
      <b-col cols="12" md="6">
        <b-skeleton animation="wave" width="85%"></b-skeleton>
        <b-skeleton animation="wave" width="55%"></b-skeleton>
        <b-skeleton animation="wave" width="70%"></b-skeleton>
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
  "name": "Customer User"
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  layout: "customer",
  data() {
    return {
      loading: true,
      fetching: false,
      currentPage: null,
      policyOptions: [],
      previousScrollPosition: null,
      dataOptions: {
        results: 10,
        page: 1,
      },
      selected: {
        policy: null,
        status: 0,
      },
      form: {
        policy: "None",
        description: null,
        policyid: null,
        policyname: "",
      },
      policyRadio: [
        {
          text: this.$t("Inherited"),
          subtitle: this.$t("Inherit group or default policy"),
          tooltip: this.$t("Inherit group or default policy"),
          testID: "user-policy-inherited",
          value: "None",
          checked: false,
          icon: "layer-backward",
          canCustomise: false,
          canDropDownSelect: false,
        },
        {
          text: this.$t("Specific"),
          subtitle: this.$t("Use a specific policy"),
          tooltip: this.$t("Use a specific"),
          testID: "user-policy-specific",
          value: "Policy",
          checked: false,
          icon: "pencil-square",
          canCustomise: false,
          canDropDownSelect: true,
        },
      ],
    };
  },
  validations: {
    form: {
      description: {},
      policyid: {},
    },
  },
  methods: {
    ...mapActions("internalUsers", [
      "fetchInternalUserAccount",
      "updateInternalUserAccount",
    ]),
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicies",
      "fetchScrollCustomerPolicies",
    ]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    onDropDownChange(item) {
      this.form.policyid = item;
      this.$v.$touch();
    },
    scrollDropDown(item) {
      const { target } = item;
      let currentScrollPosition = target.scrollTop + target.scrollHeight;

      if (currentScrollPosition > this.previousScrollPosition) {
        if (
          Math.ceil(target.scrollTop) >=
            target.scrollHeight - target.offsetHeight &&
          this.dataOptions.page > this.currentPage
        ) {
          try {
            this.fetchScrollCustomerPolicies({
              id: this.selectedUser.id,
              ...this.dataOptions,
            });
          } catch (e) {
            console.log("e", e);
          } finally {
            this.currentPage = this.dataOptions.page;
            this.dataOptions.page++;
          }
        }
      }
      this.previousScrollPosition = currentScrollPosition;
    },
    async onSubmit() {
      this.fetching = true;
      try {
        await this.updateInternalUserAccount({
          id: this.selectedUser.id,
          userId: this.selectedInternalUser.id,
          user: this.form,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },
  computed: {
    ...mapGetters("internalUsers", ["selectedInternalUser"]),
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("customerPolicies", ["customerPolicies"]),
  },
  watch: {
    selectedInternalUser: {
      handler() {
        this.form.description = this.selectedInternalUser.description;
        this.form.policyid = this.selectedInternalUser.policyid;
        this.form.policyname = this.selectedInternalUser.policyname;
        if (this.form.policyid === null) {
          this.form.policy = "None";
        } else {
          this.form.policy = "Policy";
        }
      },
      immediate: true,
    },
    customerPolicies() {
      this.policyOptions = [
        // { value: null, text: this.$t("None") },
        ...this.customerPolicies.map((p) => {
          return { value: p.id, text: p.name };
        }),
      ];
    },
  },
  async created() {
    await Promise.all([
      this.fetchInternalUserAccount({
        id: this.selectedUser.id,
        userId: this.$route.params.id,
      }),
      this.fetchCustomerPolicies({
        id: this.selectedUser.id,
        results: 10,
        sortDesc: [false],
      }),
    ]);

    this.policyOptions = [
      // { value: null, text: this.$t("Default Policy") },
      ...this.customerPolicies.map((p) => {
        return { value: p.id, text: p.name };
      }),
    ];

    this.setPageTitle(this.selectedInternalUser.name);
    this.loading = false;
  },
};
</script>
